import React, { useEffect, useState } from "react";
import Nav from "@/components/Nav";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, ArrowRight } from "lucide-react";

export default function Affiliate() {
  const [aff, setAff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/affiliate/me");
      setAff(data);
    } catch {
      setAff(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const enroll = async () => {
    setEnrolling(true);
    try {
      const { data } = await api.post("/affiliate/enroll");
      setAff(data);
      toast.success("You're in. Share your link to earn.");
    } catch {
      toast.error("Failed to enroll");
    } finally {
      setEnrolling(false);
    }
  };

  const link = aff ? `${window.location.origin}/?ref=${aff.referral_code}` : "";

  const copy = () => {
    navigator.clipboard.writeText(link);
    toast.success("Link copied");
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Nav />
      <div className="pt-28 pb-24 px-6 sm:px-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-xs uppercase tracking-widest text-[#a0a0a0] mb-2">
            Affiliate program
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter" data-testid="affiliate-title">
            Refer creators. Earn on every order.
          </h1>
          <p className="text-[#a0a0a0] mt-3 max-w-xl">
            Share your unique link, and earn commission when your referrals order clips.
          </p>

          {loading ? (
            <div className="mt-12 text-[#a0a0a0]">Loading…</div>
          ) : !aff ? (
            <div className="mt-12 squircle border border-[#2b2b2b] bg-[#111] p-10">
              <h2 className="text-2xl font-semibold">Join the program</h2>
              <p className="text-[#a0a0a0] mt-3 max-w-xl">
                Instantly get a referral link and dashboard.
              </p>
              <button
                data-testid="affiliate-enroll"
                onClick={enroll}
                disabled={enrolling}
                className="mt-6 squircle bg-white text-black px-6 py-3 font-medium hover:bg-[#f2f2f2] btn-anim inline-flex items-center gap-2 disabled:opacity-50"
              >
                {enrolling ? "Enrolling…" : "Join now"} <ArrowRight size={14} />
              </button>
            </div>
          ) : (
            <>
              <div className="mt-10 squircle border border-[#2b2b2b] bg-[#111] p-8">
                <div className="text-xs uppercase tracking-widest text-[#a0a0a0]">
                  Your referral link
                </div>
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <code
                    data-testid="affiliate-link"
                    className="flex-1 squircle-sm bg-[#0d0d0d] border border-[#2b2b2b] px-4 py-3 text-sm break-all"
                  >
                    {link}
                  </code>
                  <button
                    data-testid="affiliate-copy"
                    onClick={copy}
                    className="squircle-sm bg-white text-black px-4 py-3 text-sm hover:bg-[#f2f2f2] btn-anim inline-flex items-center gap-2"
                  >
                    <Copy size={14} /> Copy
                  </button>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
                {[
                  ["Clicks", aff.clicks],
                  ["Signups", aff.signups || 0],
                  ["Sales", aff.sales],
                  ["Earned", `$${(aff.commission_earned || 0).toFixed(2)}`],
                  ["Pending", `$${(aff.pending_payout || 0).toFixed(2)}`],
                ].map(([l, v]) => (
                  <div key={l} className="squircle border border-[#2b2b2b] bg-[#111] p-6">
                    <div className="text-xs text-[#a0a0a0] uppercase tracking-widest">{l}</div>
                    <div className="text-2xl font-bold mt-3">{v}</div>
                  </div>
                ))}
              </div>

              <div className="mt-10 squircle border border-[#2b2b2b] bg-[#111] p-8">
                <h3 className="text-lg font-semibold mb-4">Recent commissions</h3>
                {aff.commissions?.length ? (
                  <ul className="text-sm space-y-2">
                    {aff.commissions.slice().reverse().slice(0, 20).map((c, i) => (
                      <li
                        key={`${c.order_id}-${i}`}
                        className="flex items-center justify-between gap-4 py-2 border-b border-[#1f1f22] last:border-b-0"
                        data-testid={`affiliate-commission-${i}`}
                      >
                        <div className="min-w-0">
                          <div className="text-white truncate">
                            Order <code className="text-[#8ab0ff]">#{(c.order_id || "").slice(-6)}</code>
                          </div>
                          <div className="text-xs text-[#a0a0a0] mt-0.5">
                            {new Date(c.at).toLocaleDateString()} · {c.referred_user_email || "referred user"}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-white font-semibold">${(c.amount || 0).toFixed(2)}</div>
                          <div className="text-[10px] uppercase tracking-widest text-[#a0a0a0] mt-0.5">
                            {c.status || "pending"}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-[#a0a0a0] text-sm">
                    No commissions yet. Share your link — every referred order earns you 10%.
                  </div>
                )}
              </div>

              <div className="mt-6 squircle border border-[#2b2b2b] bg-[#111] p-8">
                <h3 className="text-lg font-semibold mb-4">Payout history</h3>
                {aff.payouts?.length ? (
                  <ul className="text-sm text-[#a0a0a0] space-y-1">
                    {aff.payouts.map((p, i) => (
                      <li key={i} className="flex items-center justify-between">
                        <span>{new Date(p.at).toLocaleDateString()}</span>
                        <span className="text-white">${p.amount.toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-[#a0a0a0] text-sm">No payouts yet.</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
